import { EventStreamContentType, fetchEventSource } from '@microsoft/fetch-event-source'
import { getAuthHeader } from '../../../services/api.service'
import { SseEvent } from '../state/types'

const connections = new Map<string, AbortController>()

class FatalError extends Error {
  constructor(readonly code: 'auth' | 'forbidden' | 'connection') { super(code) }
}

export interface OpenStreamArgs {
  baseUrl: string
  clientId: string
  sessionId: string
  onEvent: (e: SseEvent) => void
  onError: (msg: string) => void
}

export function openStream({ baseUrl, clientId, sessionId, onEvent, onError }: OpenStreamArgs): void {
  closeStream(clientId)
  const ctrl = new AbortController()
  connections.set(clientId, ctrl)

  fetchEventSource(`${baseUrl}/sessions/${sessionId}/events`, {
    signal: ctrl.signal,
    openWhenHidden: true,                       // keep background sessions streaming
    headers: {
      // read fresh at connect time; do not log this value
      ...getAuthHeader(localStorage.getItem('accessToken') ?? ''),
      'X-Copilot-Session-Id': sessionId,
      Accept: 'text/event-stream',
    },
    async onopen(res) {
      if (res.status === 401) throw new FatalError('auth')
      if (res.status === 403) throw new FatalError('forbidden')
      if (res.ok && res.headers.get('content-type')?.includes(EventStreamContentType)) return
      // Must stay a FatalError: onerror rethrows only those, so anything else retries every 3s forever.
      throw new FatalError('connection')
    },
    onmessage(msg) {
      if (!msg.event) return                    // keep-alive / comment frame
      onEvent({ event: msg.event, data: JSON.parse(msg.data) } as SseEvent)
    },
    onerror(err) {
      if (err instanceof FatalError) throw err  // stop retrying; surface to caller via .catch
      return 3000                               // retriable: back off 3s (library reconnects)
    },
    onclose() { /* server closed after a terminal frame; do not retry */ },
  }).catch((err) => {
    // CODES, not sentences: the caller turns them into localised operator-facing wording. These used
    // to be the literal '401' and the English 'connection lost', both of which reached the operator's
    // timeline verbatim — and the first said "401" even when the server had answered 403.
    onError(err instanceof FatalError ? err.code : 'connection')
  })
}

export function closeStream(clientId: string): void {
  connections.get(clientId)?.abort()
  connections.delete(clientId)
}

export function isStreamOpen(clientId: string): boolean {
  return connections.has(clientId)
}
